
import React from 'react'
import { Button, Box } from '@mui/material'
import { NavLink } from 'react-router'
import IconWrapper from '../IconWrapper'

const ON_DARK = 'html[data-color-mode="dark"] &'

export const launchpadMutedSx = {
  color: 'rgba(7, 9, 25, 0.5)',
  [ON_DARK]: { color: 'rgba(255, 255, 255, 0.68) !important' },
}

export const launchpadSubtitleSx = {
  ...launchpadMutedSx,
  mb: '0.25rem',
  fontSize: '1rem !important',
}

export const launchpadHoverDarkSx = {
  [ON_DARK]: {
    '&:not(:disabled):hover, &:not(:disabled).active, &:hover, &.active': {
      backgroundColor: '#2a2a2a !important',
      color: 'rgba(255, 255, 255, 0.87) !important',
      boxShadow: 'none',
    },
  },
}

/**
 * Reusable Launchpad Button Component
 *
 * This component eliminates the repeated button pattern found throughout the codebase.
 * It provides a consistent look and feel for all launchpad navigation buttons.
 *
 * @param {Object} props
 * @param {string} props.title - Button title
 * @param {string} props.subtitle - Button subtitle/description
 * @param {Array} props.icon - FontAwesome icon array [style, iconName]
 * @param {string} props.to - Route path (if using NavLink)
 * @param {function} props.onClick - Click handler (if not using NavLink)
 * @param {boolean} props.disabled - Whether button is disabled
 * @param {string} props.disabledTitle - Tooltip for disabled state
 * @param {React.ReactNode} props.children - Custom component content (overrides default button)
 */
const LaunchpadButton = ({
  title,
  subtitle,
  icon,
  to,
  onClick,
  disabled = false,
  disabledTitle,
  color,
  children,
  ...props
}) => {
  // If children are provided, render them directly (for custom components)
  if (children) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        {children}
      </div>
    )
  }

  const isClickable = Boolean(to || onClick)
  const isDisabled = Boolean(disabled || !isClickable)

  // Common button props
  const buttonProps = {
    variant: 'text',
    disabled: isDisabled,
    title: isDisabled ? disabledTitle || 'Not available' : undefined,
    sx: [
      {
        backgroundColor: "transparent",
        color: "#f8f9ff",
        border: 0,
        "&:not(:disabled):hover": { boxShadow: "0 0.22rem 0.525rem rgba(248, 249, 255, 0.4), 0 0.0625rem 0.385rem rgba(248, 249, 255, 0.54)", backgroundColor: "#f8f9ff", color: "#3b3e66" },
        "&:not(:disabled).active, &:hover": { backgroundColor: "#f8f9ff", color: "#3b3e66" },
        "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87)" },
        "html[data-color-mode=\"dark\"] &:not(:disabled):hover, html[data-color-mode=\"dark\"] &:not(:disabled).active, html[data-color-mode=\"dark\"] &:hover, html[data-color-mode=\"dark\"] &.active": {
          backgroundColor: "#2a2a2a !important",
          color: "rgba(255, 255, 255, 0.87) !important",
          boxShadow: "none",
        },
        "&:hover": { transform: "translateY(0px)" },
        boxShadow: "none !important",
        borderRadius: 0,
      },
      {
        width: '100%',
        display: 'block',
        py: '1.5rem',
        px: '0.25rem',
        textAlign: 'center',
      },
    ],
    ...props,
  }

  // If route is provided, use NavLink
  if (to) {
    return (
      <Button component={NavLink} to={to} {...buttonProps}>
        <IconWrapper icon={icon} color={color} />
        <Box sx={[{ fontWeight: 700 }, {
          fontSize: "1rem !important",
          color: "#070919",
          "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
        }]}>
          {title}
        </Box>
        <Box data-hf="muted" sx={launchpadSubtitleSx}>
          {subtitle}
        </Box>
      </Button>
    )
  }

  // Otherwise, use regular button with onClick (if provided)
  return (
    <Button
      onClick={isClickable ? onClick : undefined}
      {...buttonProps}
    >
      <IconWrapper icon={icon} color={color} />
      <Box sx={[{ fontWeight: 700 }, {
        fontSize: "1rem !important",
        color: "#070919",
        "html[data-color-mode=\"dark\"] &": { color: "rgba(255, 255, 255, 0.87) !important" },
      }]}>
        {title}
      </Box>
      <Box data-hf="muted" sx={launchpadSubtitleSx}>
        {subtitle}
      </Box>
    </Button>
  )
}

export default LaunchpadButton
